/**
 * Markdown rendering for comments and long-form fields.
 *
 * Comment bodies are stored as markdown source, never as HTML, so this is the
 * only place that turns them into DOM. The order matters:
 *
 *   attachment: refs → real URLs   (before parsing, see below)
 *   marked                         (markdown → HTML)
 *   DOMPurify                      (HTML → safe HTML)
 *
 * The server deliberately does no sanitizing of its own: tag and scheme
 * blocklists applied to raw markdown are bypassable and give false confidence.
 * This sanitizer is the real boundary.
 */
import { marked } from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({
  gfm: true,
  // Required, not cosmetic. Comments written before this feature are plain text
  // with real newlines; with marked's default a single \n collapses to a space,
  // so every historical multi-line comment would silently reflow into one
  // paragraph. `breaks: true` maps \n to <br>, matching what whitespace-pre-wrap
  // renders today.
  breaks: true,
})

const ALLOWED_TAGS = [
  'p', 'br', 'hr',
  'strong', 'em', 'del', 'code', 'pre',
  'ul', 'ol', 'li',
  'blockquote',
  'a', 'img',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
]

const ALLOWED_ATTR = ['href', 'src', 'alt', 'title']

let hookInstalled = false
function installHook() {
  if (hookInstalled) return
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer nofollow')
    }
  })
  hookInstalled = true
}

/** `![alt](attachment:41)` → the signed URL for attachment 41. */
const ATTACHMENT_REF = /(!?)\[([^\]]*)\]\(attachment:(\d+)\)/g

/**
 * Rewrites attachment references to real URLs.
 *
 * Done *before* markdown parsing rather than on the output HTML: that way the
 * src DOMPurify inspects is already the final URL and gets its normal
 * validation, instead of us editing attributes that sanitizing has already
 * blessed.
 *
 * Only ids present in `urls` are substituted — that map is built from the
 * parent's own attachment list, so a body referencing an id it shouldn't know
 * about renders as a dead link rather than fetching anything.
 */
export function resolveAttachmentRefs(source: string, urls: Map<number, string>): string {
  return source.replace(ATTACHMENT_REF, (_match, bang: string, alt: string, rawId: string) => {
    const url = urls.get(Number(rawId))
    if (!url) return bang ? `_${alt || 'attachment unavailable'}_` : alt
    return `${bang}[${alt}](${url})`
  })
}

/** Ids this body refers to, so a caller can tell inline images from loose ones. */
export function referencedAttachmentIds(source: string): number[] {
  const ids: number[] = []
  for (const match of source.matchAll(ATTACHMENT_REF)) {
    ids.push(Number(match[3]))
  }
  return ids
}

export function renderMarkdown(source: string, urls: Map<number, string> = new Map()): string {
  installHook()
  const resolved = resolveAttachmentRefs(source, urls)
  const html = marked.parse(resolved, { async: false }) as string
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR })
}
